import { useState, useRef, useEffect } from 'react'
import type { FormEvent, KeyboardEvent as ReactKeyboardEvent } from 'react'
import { captureViewport } from './qaScreenshot'
import { useLanguage } from '../../i18n/index.jsx'

const SEVERITIES = ['blocker', 'major', 'minor', 'cosmetic'] as const
const CATEGORIES = ['bug', 'ux', 'copy', 'i18n', 'a11y', 'idea'] as const

type Severity = typeof SEVERITIES[number]
type Category = typeof CATEGORIES[number]

const SEVERITY_KEYS: Record<Severity, string> = {
  blocker: 'qaFormSeverityBlocker',
  major: 'qaFormSeverityMajor',
  minor: 'qaFormSeverityMinor',
  cosmetic: 'qaFormSeverityCosmetic',
}

const CATEGORY_KEYS: Record<Category, string> = {
  bug: 'qaFormCategoryBug',
  ux: 'qaFormCategoryUx',
  copy: 'qaFormCategoryCopy',
  i18n: 'qaFormCategoryI18n',
  a11y: 'qaFormCategoryA11y',
  idea: 'qaFormCategoryIdea',
}

export interface QAContext {
  url: string
  route_name: string | null
  user_role: string
  site_id: number | null
  site_name: string | null
  viewport: string
  user_agent: string
}

type BugPayload = Record<string, unknown>

interface QABugFormProps {
  context: QAContext
  onSubmit: (payload: BugPayload) => void
  onCancel: () => void
  submitting?: boolean
}

interface Screenshot {
  b64: string
  sizeKb: number
}

export default function QABugForm({ context, onSubmit, onCancel, submitting = false }: QABugFormProps) {
  const { t } = useLanguage()
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [severity, setSeverity] = useState<Severity>('minor')
  const [category, setCategory] = useState<Category>('bug')
  const [screenshot, setScreenshot] = useState<Screenshot | null>(null)
  const [shotError, setShotError] = useState<string | null>(null)
  const [capturing, setCapturing] = useState(false)
  const titleRef = useRef<HTMLInputElement>(null)

  useEffect(() => { titleRef.current?.focus() }, [])

  const canSubmit = title.trim().length > 0 && description.trim().length > 0 && !submitting

  async function handleCapture() {
    setCapturing(true)
    setShotError(null)
    const result = await captureViewport()
    setCapturing(false)
    if (result.ok) {
      setScreenshot({ b64: result.b64 as string, sizeKb: result.sizeKb as number })
    } else {
      setShotError((result.error as string) ?? null)
      setScreenshot(null)
    }
  }

  function handleRemoveShot() {
    setScreenshot(null)
    setShotError(null)
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (!canSubmit) return
    onSubmit({
      ...context,
      title: title.trim(),
      description: description.trim(),
      severity,
      category,
      screenshot_b64: screenshot?.b64 || null,
    })
  }

  function handleKeyDown(e: ReactKeyboardEvent<HTMLFormElement>) {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault()
      handleSubmit(e as unknown as FormEvent<HTMLFormElement>)
    }
  }

  return (
    <form onSubmit={handleSubmit} onKeyDown={handleKeyDown} className="qa-bug-form">
      <div className="qa-context">
        <div className="qa-context-label">{t('qaDrawerAutoCaptured')}</div>
        <div className="qa-context-url">{context.url}</div>
        <div className="qa-context-meta">
          {t('qaDrawerRole')}: {context.user_role} · {t('qaDrawerSite')}: {context.site_name || '—'}
        </div>
      </div>

      <label className="qa-field">
        <span>{t('qaFormTitleLabel')}</span>
        <input
          ref={titleRef}
          type="text"
          maxLength={200}
          value={title}
          onChange={e => setTitle(e.target.value)}
          placeholder={t('qaFormTitlePlaceholder')}
        />
      </label>

      <label className="qa-field">
        <span>{t('qaFormDescriptionLabel')}</span>
        <textarea
          maxLength={5000}
          value={description}
          onChange={e => setDescription(e.target.value)}
          placeholder={t('qaFormDescriptionPlaceholder')}
          rows={6}
        />
      </label>

      <div className="qa-field">
        <span>{t('qaFormSeverity')}</span>
        <div className="qa-chips">
          {SEVERITIES.map(s => (
            <button
              type="button" key={s}
              className={'qa-chip' + (severity === s ? ' qa-chip-active' : '')}
              onClick={() => setSeverity(s)}
            >
              {t(SEVERITY_KEYS[s])}
            </button>
          ))}
        </div>
      </div>

      <div className="qa-field">
        <span>{t('qaFormCategory')}</span>
        <div className="qa-chips">
          {CATEGORIES.map(c => (
            <button
              type="button" key={c}
              className={'qa-chip' + (category === c ? ' qa-chip-active' : '')}
              onClick={() => setCategory(c)}
            >
              {t(CATEGORY_KEYS[c])}
            </button>
          ))}
        </div>
      </div>

      <div className="qa-field">
        {!screenshot ? (
          <button type="button" onClick={handleCapture} disabled={capturing} className="qa-shot-btn">
            {capturing ? t('qaFormCapturing') : t('qaFormCaptureScreenshot')}
          </button>
        ) : (
          <div className="qa-shot-captured">
            <span>✓ Captured ({screenshot.sizeKb} KB)</span>
            <button type="button" onClick={handleCapture}>{t('qaFormRetake')}</button>
            <button type="button" onClick={handleRemoveShot}>{t('qaFormRemove')}</button>
          </div>
        )}
        {shotError && <div className="qa-shot-error">{shotError}</div>}
      </div>

      <div className="qa-actions">
        <button type="button" onClick={onCancel}>{t('qaFormCancel')}</button>
        <button type="submit" disabled={!canSubmit}>
          {submitting ? t('qaFormSubmitting') : t('qaFormSubmit')}
        </button>
      </div>
    </form>
  )
}
